import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy, signal, computed, HostListener, viewChild, ElementRef } from '@angular/core';
import { Avatar } from '@openng/optimus-ui/avatar';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LoginService } from '../../core/services/login.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { UserNotificationsService } from '../../core/services/user-notifications.service';
import { resolveNotificationTitle } from '../../shared/models/notifications/notification-text';
import { StelaNotificationDropdownComponent } from '../../shared/layout/components/stela-notification-dropdown/stela-notification-dropdown.component';
import { StelaNotificationItem } from '../../shared/layout/models/stela-layout.models';
import { TopbarSearchPaletteComponent } from './topbar-search-palette/topbar-search-palette.component';
import { TieredMenu } from '@openng/optimus-ui/tieredmenu';
import { MenuItem } from '@openng/optimus-ui/api';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { AiAvailabilityService } from '../../core/services/ai-availability.service';
import { ChatService } from '../../core/services/chat.service';
import { ThemeStateService } from '../../core/theme/theme-state.service';

@Component({
    selector: 'app-topnavbar',
    imports: [Avatar, CommonModule, TranslatePipe, TieredMenu, TooltipModule, StelaNotificationDropdownComponent, TopbarSearchPaletteComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
      :host { display: block; }

      .topbar {
        display: flex;
        align-items: center;
        gap: 16px;
        height: var(--stela-layout-topbar-height);
        padding: 0 24px;
        background: var(--stela-surface);
        border-bottom: 1px solid var(--stela-line);
        position: sticky;
        top: 0;
        z-index: var(--z-sticky, 100);
      }

      .topbar-toggle {
        width: 32px; height: 32px;
        display: inline-flex; align-items: center; justify-content: center;
        background: transparent;
        border: 0;
        border-radius: var(--stela-radius-md);
        color: var(--stela-fg-2);
        cursor: pointer;
        transition: background var(--stela-dur-fast) var(--stela-ease);
      }
      .topbar-toggle:hover { background: var(--stela-bg); color: var(--stela-fg); }

      .topbar-search {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 36px;
        background: var(--stela-bg);
        border: 1px solid transparent;
        border-radius: var(--stela-radius-md);
        padding: 0 12px;
        min-width: 200px;
        max-width: 100%;
        flex: 0 1 520px;
        transition: border-color var(--stela-dur-fast) var(--stela-ease),
                    background var(--stela-dur-fast) var(--stela-ease);
      }
      .topbar-search:hover {
        border-color: var(--stela-line);
        background: var(--stela-surface);
      }
      .topbar-search:focus-within {
        border-color: var(--stela-gold);
        background: var(--stela-surface);
        box-shadow: 0 0 0 3px rgba(201, 168, 76, 0.12);
      }
      .topbar-search-icon { color: var(--stela-fg-3); }
      .topbar-search input {
        flex: 1 1 auto;
        border: 0;
        outline: 0;
        background: transparent;
        font-family: var(--stela-font-sans);
        font-size: 14px;
        color: var(--stela-fg);
        cursor: pointer;
        caret-color: transparent;
      }
      .topbar-search input::placeholder { color: var(--stela-fg-4); }

      .kbd {
        font-family: var(--stela-font-mono);
        font-size: 11px;
        padding: 2px 6px;
        border: 1px solid var(--stela-line);
        border-radius: var(--stela-radius-sm);
        color: var(--stela-fg-3);
        background: var(--stela-surface);
      }

      .topbar-spacer { flex: 1 1 auto; min-width: 12px; }

      .topbar-actions {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .icon-btn {
        position: relative;
        width: 36px; height: 36px;
        display: inline-flex; align-items: center; justify-content: center;
        background: transparent;
        border: 0;
        border-radius: var(--stela-radius-md);
        color: var(--stela-fg-2);
        cursor: pointer;
        transition: background var(--stela-dur-fast) var(--stela-ease),
                    color var(--stela-dur-fast) var(--stela-ease);
      }
      .icon-btn:hover { background: var(--stela-bg); color: var(--stela-fg); }
      .icon-btn.is-active {
        background: rgba(201, 168, 76, 0.12);
        color: var(--stela-gold-dark);
      }
      .icon-btn.has-dot::after {
        content: '';
        position: absolute;
        top: 8px; right: 8px;
        width: 7px; height: 7px;
        border-radius: 50%;
        background: var(--stela-gold);
        box-shadow: 0 0 0 2px var(--stela-surface);
      }

      .topbar-datetime {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        line-height: 1.2;
        justify-content: center;
        font-variant-numeric: tabular-nums;
        height: 28px;
        padding: 0 8px 0 4px;
        margin-right: 4px;
        border-right: 1px solid var(--stela-line);
      }
      .topbar-datetime .time {
        color: var(--stela-fg-1);
        font-weight: var(--stela-fw-semibold);
        font-size: 12px;
      }
      .topbar-datetime .date {
        color: var(--stela-fg-3);
        font-size: 11px;
      }

      .topbar-user {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 4px 8px 4px 4px;
        border-radius: var(--stela-radius-md);
        cursor: pointer;
        transition: background var(--stela-dur-fast) var(--stela-ease);
      }
      .topbar-user:hover { background: var(--stela-bg); }
      .topbar-user .who { display: flex; flex-direction: column; line-height: 1.2; }
      .topbar-user .who-name {
        color: var(--stela-fg-1);
        font-size: 13px;
        font-weight: var(--stela-fw-semibold);
      }
      .topbar-user .who-role {
        color: var(--stela-fg-3);
        font-size: 11px;
      }
      .topbar-user .chev {
        margin-left: 4px;
        font-size: 11px;
        color: var(--stela-fg-3);
      }

      @media (max-width: 768px) {
        .topbar-datetime { display: none; }
        .topbar-search { min-width: 0; }
        .topbar-user .who { display: none; }
      }

      /* ── UserMenu popover — bridge PrimeNG tokens to Stela 2 surface ── */
      /* Colours, border, radius and shadow come from components.tieredmenu in
         the StelaPreset; only this menu's sizing lives here. */
      :host ::ng-deep .topbar-user-menu {
        min-width: 240px;
        padding: 6px;
      }
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-submenu,
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-overlay {
        padding: 6px;
      }

      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item-link {
        padding: 8px 12px;
        border-radius: var(--stela-radius-sm);
        color: var(--stela-fg);
        gap: 10px;
        display: flex;
        align-items: center;
      }
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item-link .p-tieredmenu-item-label {
        flex: 1 1 auto;
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-suffix {
        color: var(--stela-fg-3);
        font-size: var(--stela-fs-tiny);
        font-weight: var(--stela-fw-regular);
        white-space: nowrap;
        margin-left: 12px;
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-has-suffix .p-tieredmenu-submenu-icon {
        display: none;
      }
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item-link:hover,
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item.p-focus > .p-tieredmenu-item-link {
        background: var(--stela-bg-2);
        color: var(--stela-fg-1);
      }
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item-icon {
        color: var(--stela-fg-2);
      }
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item-link:hover .p-tieredmenu-item-icon,
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-item.p-focus > .p-tieredmenu-item-link .p-tieredmenu-item-icon {
        color: var(--stela-gold);
      }
      :host ::ng-deep .topbar-user-menu .p-tieredmenu-separator {
        margin: 4px 0;
        border-top: 1px solid var(--stela-line);
      }

      /* Header item (user name) */
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-header > .p-tieredmenu-item-link {
        padding: 10px 12px 4px;
        cursor: default;
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-header .p-tieredmenu-item-label {
        font-weight: var(--stela-fw-semibold);
        color: var(--stela-fg-1);
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-header.p-disabled {
        opacity: 1;
      }

      /* Subtitle item (login · company) */
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-subtitle > .p-tieredmenu-item-link {
        padding: 0 12px 8px;
        cursor: default;
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-subtitle .p-tieredmenu-item-label {
        font-size: var(--stela-fs-tiny);
        color: var(--stela-fg-3);
        font-weight: var(--stela-fw-regular);
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-subtitle.p-disabled {
        opacity: 1;
      }

      /* Cerrar sesión en danger */
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-danger > .p-tieredmenu-item-link .p-tieredmenu-item-label,
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-danger > .p-tieredmenu-item-link .p-tieredmenu-item-icon {
        color: var(--stela-danger);
      }
      :host ::ng-deep .topbar-user-menu .topbar-user-menu-danger > .p-tieredmenu-item-link:hover {
        background: var(--stela-danger-bg);
      }
    `],
    template: `
<header class="topbar" #topbarRoot>
  <button type="button" class="topbar-toggle"
          (click)="onToggleSidebar()"
          [attr.aria-label]="'app.topbar.toggleSidebar' | translate">
    <i class="pi pi-bars"></i>
  </button>

  <div class="topbar-search">
    <i class="pi pi-search topbar-search-icon"></i>
    <input #searchInput
           type="text"
           readonly
           (focus)="onOpenSearch()"
           (click)="onOpenSearch()"
           [placeholder]="'app.topbar.search.placeholder' | translate" />
    <span class="kbd">{{ kbdShortcutLabel }}</span>
  </div>

  <topbar-search-palette #searchPalette />

  <div class="topbar-spacer"></div>

  <div class="topbar-actions">
    @if (ai.canUse('chat') || ai.canUse('beefree-copilot')) {
      <button type="button" class="icon-btn"
              [class.is-active]="chatOpen()"
              (click)="onToggleChat()"
              [attr.aria-label]="'app.topbar.assistant' | translate"
              [pTooltip]="'app.topbar.assistant' | translate">
        <i class="pi pi-sparkles"></i>
      </button>
    }

    <stela-notification-dropdown
      [notifications]="notificationItems()"
      [badgeCount]="userNotificationsService.unreadCount()"
      (notificationClick)="onNotificationItemClick($event)"
      (viewAll)="onOpenNotifications()" />

    <div class="topbar-datetime" aria-hidden="true">
      <span class="time">{{ nowTime() }}</span>
      <span class="date">{{ nowDate() }}</span>
    </div>

    <div class="topbar-user" (click)="userMenu.toggle($event)" tabindex="0"
         (keydown.enter)="userMenu.toggle($event)">
      <p-avatar [label]="userInitials()" shape="circle" styleClass="size-8! shrink-0 bg-(--stela-aubergine)! text-(--stela-gold)! font-(family-name:--stela-font-display) text-[13px]! font-bold!" />
      <div class="who">
        <span class="who-name">{{ userName() }}</span>
        <span class="who-role">{{ userRole() }}</span>
      </div>
      <i class="pi pi-chevron-down chev"></i>
    </div>

    <p-tieredmenu #userMenu [popup]="true" [model]="userMenuItems()" appendTo="body"
                  styleClass="topbar-user-menu">
      <ng-template #item let-item let-hasSubmenu="hasSubmenu">
        <a class="p-tieredmenu-item-link">
          @if (item.icon) {
            <span class="p-tieredmenu-item-icon" [class]="item.icon"></span>
          }
          <span class="p-tieredmenu-item-label">{{ item.label }}</span>
          @if (item.state?.suffix) {
            <span class="topbar-user-menu-suffix">{{ item.state.suffix }}</span>
          }
          @if (hasSubmenu) {
            <span class="p-tieredmenu-submenu-icon pi pi-angle-right"></span>
          }
        </a>
      </ng-template>
    </p-tieredmenu>
  </div>
</header>
`
})
export class TopnavbarComponent implements OnInit, OnDestroy {
    // Stela 2 topbar — new template bindings
    readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
    readonly searchPalette = viewChild<TopbarSearchPaletteComponent>('searchPalette');

    readonly chatOpen = signal(false);

    private readonly notificationVariantBySeverity: Record<string, string> = {
        info: 'info', success: 'success', warn: 'warning', error: 'danger'
    };
    private readonly notificationIconFallback: Record<string, string> = {
        info: 'pi pi-info-circle', success: 'pi pi-check-circle', warning: 'pi pi-exclamation-triangle', danger: 'pi pi-times-circle'
    };
    readonly notificationItems = computed<StelaNotificationItem[]>(() =>
        this.userNotificationsService.notifications().map((n) => {
            const variant = this.notificationVariantBySeverity[n.severity ?? 'info'] ?? 'info';
            // The text comes from the i18n key with its parameters, which is what makes a
            // grouped row read as "12 invitados han confirmado" instead of repeating the
            // first one's line.
            return {
                id: String(n.id),
                icon: n.icon || this.notificationIconFallback[variant],
                message: resolveNotificationTitle(n, this.translate),
                timestamp: n.last_event_at ?? n.created_at,
                variant
            };
        })
    );

    private now = signal(new Date());
    readonly nowTime = computed(() =>
        this.now().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    );
    readonly nowDate = computed(() =>
        this.now().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    );

    readonly userName = signal('Administrador');
    readonly userRole = signal('Diaple · Protocolo');
    readonly userInitials = computed(() => {
        const n = this.userName().trim();
        if (!n) return 'U';
        const parts = n.split(/\s+/);
        return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'U';
    });

    private static readonly LANGUAGE_OPTIONS: { code: string; label: string }[] = [
        { code: 'ES', label: 'Español' },
        { code: 'EN', label: 'English' },
        { code: 'CA', label: 'Català' },
        { code: 'EU', label: 'Euskara' },
        { code: 'FR', label: 'Français' },
        { code: 'GL', label: 'Galego' },
    ];

    readonly userMenuItems = computed<MenuItem[]>(() => {
        const isDark = this.themeKit.darkMode();
        const currentLang = (this.currentLanguage() || 'ES').toUpperCase();
        const langLabel = TopnavbarComponent.LANGUAGE_OPTIONS.find(o => o.code === currentLang)?.label ?? currentLang;
        return [
            { label: this.userName(), disabled: true, styleClass: 'topbar-user-menu-header' },
            ...(this.userSubtitle()
                ? [{ label: this.userSubtitle(), disabled: true, styleClass: 'topbar-user-menu-subtitle' } as MenuItem]
                : []),
            { separator: true },
            { label: this.t('app.profile', 'Mi perfil'), icon: 'pi pi-user', command: () => this.onProfile() },
            { label: this.t('app.preferences', 'Preferencias'), icon: 'pi pi-cog', command: () => this.onPreferences() },
            { separator: true },
            {
                label: this.t('app.theme', 'Tema'),
                icon: isDark ? 'pi pi-moon' : 'pi pi-sun',
                styleClass: 'topbar-user-menu-has-suffix',
                command: () => this.onToggleTheme(),
                state: {
                    suffix: isDark
                        ? this.t('app.theme.dark', 'Oscuro')
                        : this.t('app.theme.light', 'Claro'),
                },
            } as MenuItem,
            {
                label: this.t('app.languages', 'Idioma'),
                icon: 'pi pi-globe',
                styleClass: 'topbar-user-menu-has-suffix',
                state: { suffix: `${langLabel} · ${currentLang}` },
                items: TopnavbarComponent.LANGUAGE_OPTIONS.map(opt => ({
                    label: opt.label,
                    icon: opt.code === currentLang ? 'pi pi-check' : 'pi pi-fw',
                    command: () => this.changeLanguage(opt.code, 0),
                })),
            } as MenuItem,
            { separator: true },
            {
                label: this.t('app.exit', 'Cerrar sesión'),
                icon: 'pi pi-sign-out',
                styleClass: 'topbar-user-menu-danger',
                command: () => this.onLogout(),
            },
        ];
    });

    readonly userSubtitle = computed(() => {
        // Prefer email; fall back to login when it looks like an email; otherwise empty.
        const email = (this.loginService.get('email') || '').trim();
        if (email) return email;
        const login = (this.loginService.get('user') || '').trim();
        return login.includes('@') ? login : '';
    });

    private t(key: string, fallback: string): string {
        const v = this.translateService?.instant?.(key);
        return v && v !== key ? v : fallback;
    }

    readonly kbdShortcutLabel = navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K';

    private clockTimer: ReturnType<typeof setInterval> | undefined;

    // Current language (kept: referenced by tests and changeLanguage)
    readonly currentLanguage = signal('ES');

    public chatService = inject(ChatService);
    protected readonly ai = inject(AiAvailabilityService);
    public userNotificationsService = inject(UserNotificationsService);
    private readonly translate = inject(TranslateService);
    private sidebarService = inject(SidebarService);
    public themeKit = inject(ThemeStateService);

    constructor(
        public loginService: LoginService,
        public translateService: TranslateService,
        public router: Router
    ) {}

    ngOnInit(): void {
        void this.ai.ensureLoaded();
        this.themeKit.initializeFromDom();
        this.currentLanguage.set(this.loginService.get('language') || 'ES');

        // Populate user identity from LoginService
        const storedUserName = this.loginService.get('userName') || this.loginService.get('user');
        if (storedUserName) {
            this.userName.set(storedUserName);
        }
        const companyId = this.loginService.get('companyName') || this.loginService.get('company') || '';
        if (companyId) {
            this.userRole.set(companyId);
        }

        // Stela 2 clock — refresh datetime signal every minute
        this.clockTimer = setInterval(() => this.now.set(new Date()), 60_000);
    }

    ngOnDestroy(): void {
        if (this.clockTimer) {
            clearInterval(this.clockTimer);
        }
    }

    @HostListener('document:keydown', ['$event'])
    onGlobalKey(e: KeyboardEvent): void {
        const isShortcut = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
        if (isShortcut) {
            e.preventDefault();
            this.onOpenSearch();
        }
    }

    onToggleSidebar(): void {
        this.sidebarService.toggle();
    }

    onToggleChat(): void {
        this.chatService.toggle();
        this.chatOpen.set(this.chatService.visible());
    }

    onOpenNotifications(): void {
        this.router.navigate(['/user_notifications/list']);
    }

    onNotificationItemClick(item: StelaNotificationItem): void {
        const id = Number(item.id);
        this.userNotificationsService.markAsRead(id);

        const source = this.userNotificationsService.notifications().find((n) => n.id === id);
        if (source?.route) {
            this.router.navigate([source.route]);
        }
    }

    onOpenSearch(): void {
        this.searchPalette()?.show();
    }

    onToggleTheme(): void {
        this.themeKit.toggleDarkMode();
        localStorage.setItem('stela-dark-mode', this.themeKit.darkMode() ? 'true' : 'false');
    }

    onProfile(): void {
        this.router.navigate(['/users/profile']);
    }

    onPreferences(): void {
        this.router.navigate(['/user_notifications/preferences']);
    }

    onLogout(): void {
        this.loginService.closeSession();
    }

    changeLanguage(code: string, _languageId: number): void {
        this.translateService.use(code);
        this.currentLanguage.set(code);
        this.loginService.set('language', code);
        // Reload to apply language changes
        window.location.reload();
    }

    closeSession(): void {
        this.loginService.closeSession();
    }
}
